import { useState } from "react";
import { Download, Upload, Loader2, FileKey2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const KEYSTORE_NAME_RE = /^UTC--[0-9T:\-.Z]+--(?:0x)?[0-9a-fA-F]{40}(?:\.json)?$/;
const NETWORKS = [
  { value: "ethereum", label: "Ethereum" }, { value: "polygon", label: "Polygon" }, { value: "bsc", label: "BNB Chain" },
];

function saveFile(name, text) {
  const url = URL.createObjectURL(new Blob([text], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function fallbackName(address) {
  const ts = new Date().toISOString().replace(/:/g, "-").replace(/\.(\d{3})Z$/, ".$1000000Z");
  return `UTC--${ts}--${String(address || "").toLowerCase().replace(/^0x/, "")}`;
}

function SimpleSelect({ value, onChange, options, testid, placeholder }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-muted/40 border-border"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-card border-border text-foreground">
        {options.map((o) => <SelectItem key={o.value} value={String(o.value)} className="focus:bg-accent">{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export const KeystoreDownloadButton = ({ row }) => {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const res = await api.post(`/crypto-wallets/${row.id}/keystore`, { password: pw }, { responseType: "text", transformResponse: (d) => d });
      const ks = JSON.parse(res.data);
      const name = res.headers["x-keystore-filename"] || fallbackName(ks.address);
      saveFile(name, res.data);
      toast.success("Keystore downloaded");
      setOpen(false); setPw(""); setPw2("");
    } catch (err) {
      let detail = err.response?.data;
      try { detail = JSON.parse(detail).detail; } catch { /* plain */ }
      toast.error(apiError(detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} disabled={!row.has_private_key}
        title={row.has_private_key ? "Download Keystore" : "No private key stored"}
        data-testid="keystore-download-button" className="text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10 mr-1">
        <Download className="h-4 w-4 mr-1" /> Keystore
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Download Keystore</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Ethereum V3 keystore (scrypt + AES-128-CTR) for <span className="font-mono break-all">{row.address}</span>. You'll need this password to import it in MetaMask, geth or CITIDAN.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Keystore password</Label>
              <Input data-testid="keystore-download-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="bg-muted/40 border-border" placeholder="Min 8 characters" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Confirm password</Label>
              <Input data-testid="keystore-download-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="bg-muted/40 border-border" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-muted-foreground">Cancel</Button>
            <Button data-testid="keystore-download-confirm" onClick={download} disabled={busy} className="bg-indigo-500 hover:bg-indigo-600 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" />Download</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const EMPTY = { owner_type: "company", owner_id: "", currency: "ETH", network: "ethereum", password: "" };

export const KeystoreImportButton = ({ lookups, onImported }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const owners = (lookups[form.owner_type === "company" ? "companies" : "customers"] || []).map((o) => ({ value: o.id, label: o.name }));
  const assets = (lookups.currencies || []).filter((c) => c.type === "crypto").map((c) => ({ value: c.code, label: c.code }));
  const nameOk = file && KEYSTORE_NAME_RE.test(file.name);

  const submit = async () => {
    if (!nameOk) return toast.error("File name must follow UTC--{timestamp}--{wallet_address}");
    if (!form.owner_id) return toast.error("Select an owner");
    setBusy(true);
    try {
      let keystore;
      try { keystore = JSON.parse(await file.text()); } catch { throw new Error("File is not valid keystore JSON"); }
      await api.post("/crypto-wallets/import-keystore", { ...form, filename: file.name, keystore });
      toast.success("Wallet imported from keystore");
      setOpen(false); setFile(null); setForm(EMPTY);
      onImported();
    } catch (err) {
      toast.error(err.response ? apiError(err.response.data?.detail) : err.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button data-testid="keystore-import-button" onClick={() => setOpen(true)} variant="outline"
        className="border-border bg-card text-foreground hover:bg-accent">
        <Upload className="h-4 w-4 mr-2" /> Import Keystore
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Import Keystore</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Upload an Ethereum keystore file named <span className="font-mono">UTC--&#123;timestamp&#125;--&#123;wallet_address&#125;</span>. The private key is decrypted with your password and stored encrypted.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-1">
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs text-muted-foreground">Keystore file</Label>
              <label className="flex items-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-3 cursor-pointer hover:bg-accent/50 transition-colors">
                <FileKey2 className="h-5 w-5 text-indigo-500 shrink-0" />
                <span data-testid="keystore-import-filename" className="text-xs font-mono break-all">{file ? file.name : "Choose UTC--… file"}</span>
                <input data-testid="keystore-import-file" type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
              {file && !nameOk && <p data-testid="keystore-import-name-error" className="text-xs text-red-500">File name must follow UTC--&#123;timestamp&#125;--&#123;wallet_address&#125;</p>}
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs text-muted-foreground">Keystore password</Label>
              <Input data-testid="keystore-import-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="bg-muted/40 border-border" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Owner Type</Label>
              <SimpleSelect testid="keystore-import-owner-type" value={form.owner_type} onChange={(v) => { set("owner_type", v); set("owner_id", ""); }}
                options={[{ value: "company", label: "Company" }, { value: "customer", label: "Customer" }]} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Owner</Label>
              <SimpleSelect testid="keystore-import-owner" value={form.owner_id} onChange={(v) => set("owner_id", v)} options={owners} placeholder="Select owner" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Asset</Label>
              <SimpleSelect testid="keystore-import-asset" value={form.currency} onChange={(v) => set("currency", v)}
                options={assets.length ? assets : [{ value: "ETH", label: "ETH" }]} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Network</Label>
              <SimpleSelect testid="keystore-import-network" value={form.network} onChange={(v) => set("network", v)} options={NETWORKS} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-muted-foreground">Cancel</Button>
            <Button data-testid="keystore-import-submit" onClick={submit} disabled={busy || !file || !form.password}
              className="bg-indigo-500 hover:bg-indigo-600 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Import wallet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
